import { useState } from 'react';

import Button from 'ui/Button';
import Card from 'ui/Card';
import Icon from 'ui/Icon';
import { HStack, VStack } from 'ui/Layout';
import Link from 'ui/Link';
import Slider from 'ui/Slider';
import { Text } from 'ui/Typography';

import { Price, ProductMetadata } from '../../../generated-api';
import { usePopupWindowOpener } from '../../../utils/popup';
import { pathToSubscriptionCheckout } from '../../../utils/routes';

type Props = {
  price: Price;
  metadata: ProductMetadata;
  isAnnual: boolean;
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(price / 100);
};

const StripePriceCard: React.FC<Props> = ({ price, metadata, isAnnual }) => {
  const isTieredPrice = price.billing_scheme === 'tiered';
  const tieredBasePrice =
    price.tiers?.find((tier) => tier.flat_amount)?.flat_amount ?? 0;
  const tieredBaseSeats = price.tiers?.find((tier) => tier.up_to)?.up_to ?? 1;
  const tieredPricePerAdditionalSeat =
    price.tiers?.find((tier) => tier.unit_amount)?.unit_amount ?? 0;
  const perUnitPrice = price.unit_amount ?? 0;
  const maxSeats = 50;

  const [seatsSelected, setSeatsSelected] = useState(tieredBaseSeats);
  const stripeCheckout = usePopupWindowOpener(
    pathToSubscriptionCheckout(price.id, seatsSelected)
  );

  const annualPrice = isTieredPrice
    ? tieredBasePrice +
      (seatsSelected - tieredBaseSeats) * tieredPricePerAdditionalSeat
    : perUnitPrice;

  const header = metadata.is_default
    ? { bg: 'green.800', borderColor: 'green.800', color: 'white' }
    : { bg: 'gray.50', borderColor: 'gray.400', color: 'black' };

  return (
    <Card.Root
      border="1px solid"
      borderColor={header.borderColor}
      borderRadius="0.5rem"
      overflow="hidden"
    >
      <Card.Header
        {...header}
        borderBottom="1px solid"
        borderBottomColor={header.borderColor}
        p={4}
      >
        <Text fontSize="xl">{metadata.name}</Text>
        <Text fontSize="xs">{metadata.description}</Text>
      </Card.Header>
      <Card.Body p={4}>
        <Text fontSize="3xl" fontWeight="medium">
          {seatsSelected > maxSeats
            ? '$ -'
            : formatPrice((annualPrice ?? 0) / (isAnnual ? 12 : 1))}
        </Text>
        {seatsSelected > maxSeats ? (
          <Link display="block" fontSize="sm" href="mailto:sales@sankeyart.com">
            Get in touch for a quote
          </Link>
        ) : (
          <Text color="gray.700" fontSize="sm" mt={1}>
            {isAnnual ? 'per month, paid annually' : 'per month'}
          </Text>
        )}
        <HStack mt={2}>
          <Text
            color="gray.600"
            fontSize="sm"
            fontWeight="medium"
            minW="65px"
            mr={1}
            whiteSpace="nowrap"
          >
            {seatsSelected > maxSeats
              ? `${maxSeats}+ seats`
              : seatsSelected > 1
                ? `${seatsSelected} seats`
                : '1 seat'}
          </Text>
          <Slider
            value={[seatsSelected]}
            onValueChange={(e) => setSeatsSelected(e.value[0])}
            min={tieredBaseSeats}
            max={maxSeats + 1}
            step={1}
            display={isTieredPrice ? 'block' : 'none'}
            w="100%"
          />
        </HStack>
        <VStack alignItems="flex-start" mt={4} gap={0.5}>
          {metadata.features.map((feature) => (
            <HStack key={feature}>
              <Icon color="green.600" icon="circle-check-regular" />
              <Text fontSize="sm">{feature}</Text>
            </HStack>
          ))}
          {metadata.excluded_features.map((feature) => (
            <HStack color="gray.400" key={feature}>
              <Icon icon="circle-xmark-regular" />
              <Text fontSize="sm">{feature}</Text>
            </HStack>
          ))}
        </VStack>
      </Card.Body>
      <Card.Footer p={4}>
        <Button
          variant="primary-solid"
          onClick={stripeCheckout}
          disabled={seatsSelected > maxSeats}
          autoFocus={metadata.is_default}
          size="lg"
          w="100%"
        >
          Subscribe
        </Button>
      </Card.Footer>
    </Card.Root>
  );
};

export default StripePriceCard;
